import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  CaretLeft, Truck, CheckCircle, ClipboardText, Warning, Clock,
  Thermometer, Drop, Lightbulb, ShieldCheck, PenNib, FileText,
  Hash, MapPin, Package, ArrowsClockwise, UserCircle, Wrench, Buildings, TrashSimple,
  Bell, CloudArrowUp,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import RiskCard from "@/components/RiskCard";
import RouteView from "@/components/RouteView";
import GeofenceAssignmentsEditor from "@/components/GeofenceAssignmentsEditor";
import DeviceAssignmentsEditor from "@/components/DeviceAssignmentsEditor";
import ShipmentAuditTimeline from "@/components/ShipmentAuditTimeline";
import LifecycleBanner from "@/components/LifecycleBanner";
import EditShipmentDialog from "@/components/EditShipmentDialog";
import NotificationPreviewTab from "@/components/NotificationPreviewTab";
import ShipmentLoggerImportDialog from "@/components/ShipmentLoggerImportDialog";

// Driver icon (Steering not available in phosphor-icons)
const Steering = UserCircle;

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLES = {
  planned:                   { bg: "bg-zinc-500/15",   text: "text-zinc-300",    dot: "bg-zinc-400",    label: "Planned" },
  released:                  { bg: "bg-sky-500/15",    text: "text-sky-300",     dot: "bg-sky-400",     label: "Released" },
  in_transit:                { bg: "bg-teal-500/15",   text: "text-teal-400",    dot: "bg-teal-400",    label: "In Transit" },
  received:                  { bg: "bg-blue-500/15",   text: "text-blue-300",    dot: "bg-blue-400",    label: "Received" },
  inspected:                 { bg: "bg-indigo-500/15", text: "text-indigo-300",  dot: "bg-indigo-400",  label: "Inspected" },
  corrective_action_required:{ bg: "bg-amber-500/15",  text: "text-amber-300",   dot: "bg-amber-400",   label: "Corrective Action Required" },
  qa_review:                 { bg: "bg-amber-500/15",  text: "text-amber-300",   dot: "bg-amber-400",   label: "QA Review" },
  approved:                  { bg: "bg-emerald-500/15",text: "text-emerald-300", dot: "bg-emerald-400", label: "Approved" },
  delivered:                 { bg: "bg-emerald-500/15",text: "text-emerald-400", dot: "bg-emerald-400", label: "Delivered" },
  delayed:                   { bg: "bg-amber-500/15",  text: "text-amber-400",   dot: "bg-amber-400",   label: "Delayed" },
  excursion_review:          { bg: "bg-amber-500/15",  text: "text-amber-300",   dot: "bg-amber-400",   label: "Excursion Review" },
  quarantined:               { bg: "bg-red-500/15",    text: "text-red-300",     dot: "bg-red-500",     label: "Quarantined" },
  rejected:                  { bg: "bg-red-500/15",    text: "text-red-400",     dot: "bg-red-500",     label: "Rejected" },
  closed:                    { bg: "bg-zinc-700/30",   text: "text-zinc-400",    dot: "bg-zinc-500",    label: "Closed" },
};

export default function ShipmentDetail({ shipmentId, onBack, onSelectDevice }) {
  const { user } = useAuth();
  const [shipment, setShipment] = useState(null);
  const [compliance, setCompliance] = useState(null);
  const [shipmentRisk, setShipmentRisk] = useState(null);
  const [driverRisk, setDriverRisk] = useState(null);
  const [tab, setTab] = useState(() => {
    // Deep-link support: /shipments/{id}#route → open Route tab directly (used by PDF "Show on Map" link)
    if (typeof window !== "undefined") {
      const h = (window.location.hash || "").replace(/^#/, "");
      if (["overview", "route", "parties", "notifications", "compliance", "audit"].includes(h)) return h;
    }
    return "overview";
  });
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);
  const [showSignDialog, setShowSignDialog] = useState(false);
  const [signName, setSignName] = useState("");
  const [showCorrectiveDialog, setShowCorrectiveDialog] = useState(false);
  const [statusDialogOpen, setStatusDialogOpen] = useState(null);  // "quarantined" | "excursion_review" | "delivered"
  const [statusReason, setStatusReason] = useState("");
  const [showImportLogger, setShowImportLogger] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showArchiveDialog, setShowArchiveDialog] = useState(false);
  const [archiving, setArchiving] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    // Small retry helper — preview environments (Cloudflare in front) sometimes
    // 429 a burst of parallel requests. One quiet retry buys us reliability
    // without flooding the user with errors.
    const safeGet = async (url, { primary = false } = {}) => {
      for (let attempt = 0; attempt < (primary ? 3 : 2); attempt++) {
        try {
          return await axios.get(url);
        } catch (e) {
          const status = e.response?.status;
          const transient = !status || status === 429 || status >= 500;
          if (!transient || attempt === (primary ? 2 : 1)) {
            if (primary) throw e;
            return null;
          }
          await new Promise((r) => setTimeout(r, 500 * (attempt + 1)));
        }
      }
      return null;
    };
    try {
      // Fire the primary request first — everything else hangs off the shipment.
      const s = await safeGet(`${API}/shipments/${shipmentId}`, { primary: true });
      setShipment(s.data);
      // Now fire secondary requests in parallel (these tolerate failure).
      const [c, r] = await Promise.all([
        safeGet(`${API}/shipments/${shipmentId}/compliance`),
        safeGet(`${API}/ai/shipment-risk/${shipmentId}`),
      ]);
      setCompliance(c?.data || null);
      setShipmentRisk(r?.data || null);
      if (s.data?.driver_name) {
        safeGet(`${API}/ai/driver-risk/${encodeURIComponent(s.data.driver_name)}`)
          .then((res) => setDriverRisk(res?.data || null));
      } else {
        setDriverRisk(null);
      }
    } catch (e) {
      toast.error(e.response?.status === 404 ? "Shipment not found" : "Failed to load shipment");
    } finally {
      setLoading(false);
    }
  }, [shipmentId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Sync tab → URL hash so the Route view is shareable / PDF-link-resolvable
  useEffect(() => {
    if (typeof window === "undefined") return;
    const desired = tab === "overview" ? "" : `#${tab}`;
    if (window.location.hash !== desired) {
      window.history.replaceState(null, "", `${window.location.pathname}${window.location.search}${desired}`);
    }
  }, [tab]);

  // Listen for in-shipment tab navigation. The Activation Wizard fires this
  // when "Go to Parties" (or similar) is clicked so we switch the current
  // shipment's tab instead of opening the global admin modal.
  useEffect(() => {
    const handler = (e) => {
      const t = e?.detail?.tab;
      if (t && ["overview", "route", "parties", "notifications", "compliance", "audit"].includes(t)) {
        setTab(t);
      }
    };
    window.addEventListener("holotrak:ship-tab-nav", handler);
    return () => window.removeEventListener("holotrak:ship-tab-nav", handler);
  }, []);

  const lifecycleAction = async (path, label) => {
    setActing(true);
    try {
      await axios.post(`${API}/shipments/${shipmentId}/${path}`);
      toast.success(`Shipment ${label}`);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || `Failed to ${label}`);
    } finally {
      setActing(false);
    }
  };

  const submitStatusChange = async () => {
    if (!statusDialogOpen) return;
    setActing(true);
    try {
      await axios.put(`${API}/shipments/${shipmentId}`, {
        status: statusDialogOpen,
        notes: statusReason.trim()
          ? `[${statusDialogOpen.replace(/_/g, " ").toUpperCase()}] ${statusReason.trim()}`
          : undefined,
      });
      toast.success(`Status changed to ${statusDialogOpen.replace(/_/g, " ")}`);
      setStatusDialogOpen(null);
      setStatusReason("");
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Status change failed");
    } finally {
      setActing(false);
    }
  };

  const handleSign = async () => {
    if (!signName.trim()) { toast.error("Enter your name"); return; }
    setActing(true);
    try {
      await axios.post(`${API}/shipments/${shipmentId}/sign`, {
        signed_by: signName.trim(),
        signature_text: signName.trim(),
      });
      toast.success("Shipment signed");
      setShowSignDialog(false);
      setSignName("");
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to sign");
    } finally {
      setActing(false);
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-pulse text-teal-400 font-mono text-sm">Loading shipment...</div></div>;
  if (!shipment) return <div className="text-zinc-500 text-sm">Shipment not found</div>;

  const sev = STATUS_STYLES[shipment.status] || STATUS_STYLES.planned;
  const tabs = [
    { key: "overview", label: "Overview", icon: ClipboardText },
    { key: "route", label: "Route", icon: MapPin },
    { key: "parties", label: "Parties & Custody", icon: Buildings },
    { key: "notifications", label: "Notification Preview", icon: Bell },
    { key: "compliance", label: "Compliance Report", icon: ShieldCheck },
    { key: "audit", label: "Audit Trail", icon: FileText },
  ];

  return (
    <div className="space-y-4" data-testid="shipment-detail">
      {/* Back */}
      <button onClick={onBack} className="text-xs font-mono text-teal-400 hover:text-teal-300 flex items-center gap-1"
        data-testid="ship-back-btn">
        <CaretLeft size={14} /> Back to Shipments
      </button>

      {/* Phase 47 — Lifecycle gate banner + Readiness card. Renders only
          when the shipment is NOT yet ACTIVE so it never adds noise on a
          fully operational shipment. */}
      <LifecycleBanner
        shipment={shipment}
        onActivated={(newState) => setShipment(s => ({ ...s, lifecycle_state: newState }))}
      />

      <HeldSummariesBanner
        shipmentId={shipment.id}
        user={user}
        lifecycleState={shipment.lifecycle_state}
        onChanged={fetchAll}
      />

      {/* Header card */}
      <Card className="bg-zinc-900 border-zinc-800 p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-xl font-semibold text-zinc-50">{shipment.name}</h2>
              <Badge className={`text-[10px] uppercase tracking-wider font-mono ${sev.bg} ${sev.text} border-0`}>
                <span className={`w-1.5 h-1.5 rounded-full ${sev.dot} mr-1`} />{sev.label}
              </Badge>
              {shipment.signature?.signed_by && (
                <Badge className="text-[9px] bg-emerald-500/20 text-emerald-400 border-0">
                  <ShieldCheck size={9} className="mr-0.5" /> Signed
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-2 mt-1 text-[10px] font-mono text-zinc-500">
              <Hash size={10} />
              <span data-testid="shipment-id">{shipment.id}</span>
            </div>
            {/* References */}
            {(shipment.references || []).length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-3" data-testid="shipment-references">
                {shipment.references.map((r, i) => (
                  <span key={i} className="text-[10px] font-mono px-2 py-1 rounded-sm bg-zinc-800 text-zinc-300 border border-zinc-700">
                    <span className="text-zinc-500">{r.label || r.type}:</span> {r.value}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Lifecycle actions */}
          <div className="flex items-center gap-2">
            {shipment.status === "planned" && (
              <Button onClick={() => lifecycleAction("start", "started")} disabled={acting}
                className="bg-teal-500 hover:bg-teal-600 text-zinc-950 text-xs h-9"
                data-testid="ship-start-btn">
                <Truck size={12} className="mr-1" /> Start Transit
              </Button>
            )}
            {shipment.status === "in_transit" && (
              <Button onClick={() => lifecycleAction("deliver", "delivered")} disabled={acting}
                className="bg-emerald-500 hover:bg-emerald-600 text-zinc-950 text-xs h-9"
                data-testid="ship-deliver-btn">
                <CheckCircle size={12} className="mr-1" /> Mark Delivered
              </Button>
            )}
            {shipment.status === "delivered" && shipment.signature?.required && !shipment.signature?.signed_by && (
              <Button onClick={() => setShowSignDialog(true)} disabled={acting}
                className="bg-teal-500 hover:bg-teal-600 text-zinc-950 text-xs h-9"
                data-testid="ship-sign-btn">
                <PenNib size={12} className="mr-1" /> Sign
              </Button>
            )}
            {["in_transit", "delivered", "corrective_action_required", "qa_review"].includes(shipment.status) && (
              <Button onClick={() => setShowCorrectiveDialog(true)} disabled={acting}
                variant="outline"
                className="border-amber-700/60 text-amber-300 hover:bg-amber-500/10 hover:text-amber-200 text-xs h-9"
                data-testid="ship-corrective-btn">
                <Wrench size={12} className="mr-1" /> Log Action
              </Button>
            )}
            {(shipment.status === "in_transit" || shipment.status === "delivered") && shipment.status !== "quarantined" && (
              <Button onClick={() => setStatusDialogOpen("excursion_review")} disabled={acting}
                variant="outline"
                className="border-amber-700/60 text-amber-300 hover:bg-amber-500/10 text-xs h-9"
                data-testid="ship-review-btn">
                Mark Under Review
              </Button>
            )}
            {(shipment.status === "in_transit" || shipment.status === "delivered" || shipment.status === "excursion_review") && shipment.status !== "quarantined" && (
              <Button onClick={() => setStatusDialogOpen("quarantined")} disabled={acting}
                variant="outline"
                className="border-red-700/60 text-red-300 hover:bg-red-500/10 text-xs h-9"
                data-testid="ship-quarantine-btn">
                Quarantine
              </Button>
            )}
            {shipment.status === "quarantined" && (
              <Button onClick={() => setStatusDialogOpen("delivered")} disabled={acting}
                variant="outline"
                className="border-emerald-700/60 text-emerald-300 hover:bg-emerald-500/10 text-xs h-9"
                data-testid="ship-release-btn">
                Release from Quarantine
              </Button>
            )}
            <EvidencePackageButton shipmentId={shipment.id} />
            {!["cancelled", "failed", "quarantined"].includes(shipment.status) && (
              <Button onClick={() => setShowImportLogger(true)} variant="outline"
                className="border-sky-700/60 text-sky-300 hover:bg-sky-500/10 hover:text-sky-200 text-xs h-9"
                data-testid="ship-import-logger-btn">
                <CloudArrowUp size={12} className="mr-1" /> Import Logger
              </Button>
            )}
            <Button onClick={() => setShowEditDialog(true)} variant="outline"
              className="border-zinc-700 hover:bg-zinc-800 text-zinc-300 text-xs h-9"
              data-testid="ship-edit-btn">
              <PenNib size={12} className="mr-1" /> Edit
            </Button>
            <Button onClick={() => setShowArchiveDialog(true)} variant="outline"
              className="border-red-700/60 text-red-300 hover:bg-red-500/10 hover:text-red-200 text-xs h-9"
              data-testid="ship-archive-btn">
              <TrashSimple size={12} className="mr-1" /> Archive
            </Button>
            <button onClick={fetchAll} className="p-2 text-zinc-500 hover:text-teal-400" data-testid="ship-refresh">
              <ArrowsClockwise size={14} />
            </button>
          </div>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-5 pt-4 border-t border-zinc-800">
          <KPI label="Customer" value={shipment.customer || "—"} />
          <KPI label="Driver" value={shipment.driver_name || "—"} />
          <KPI label="Devices" value={(shipment.device_names || []).length} />
          <KPI label="Temp Range" value={`${shipment.temp_min}–${shipment.temp_max}°F`} />
          <KPI label="Compliance" value={shipment.compliance_score != null ? `${shipment.compliance_score}%` : "—"}
            color={shipment.compliance_score >= 95 ? "text-emerald-400" : shipment.compliance_score >= 80 ? "text-amber-400" : shipment.compliance_score != null ? "text-red-400" : "text-zinc-300"} />
        </div>
      </Card>

      {/* Tabs */}
      <div className="flex border-b border-zinc-800 gap-1">
        {tabs.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            data-testid={`ship-tab-${t.key}`}
            className={`flex items-center gap-1.5 px-4 py-2 text-xs font-medium transition-colors border-b-2 ${
              tab === t.key
                ? "text-teal-400 border-teal-400"
                : "text-zinc-500 border-transparent hover:text-zinc-300"
            }`}>
            <t.icon size={13} weight="duotone" />{t.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {tab === "overview" && (
        <OverviewTab shipment={shipment} compliance={compliance}
          shipmentRisk={shipmentRisk} driverRisk={driverRisk}
          onSelectDevice={onSelectDevice} onReload={fetchAll} />
      )}
      {tab === "route" && (
        <RouteView shipmentId={shipmentId} />
      )}
      {tab === "parties" && (
        <PartiesAndCustodyTab shipment={shipment} shipmentId={shipmentId} onUpdated={fetchAll} />
      )}
      {tab === "notifications" && (
        <NotificationPreviewTab shipmentId={shipmentId} reloadKey={shipment?.lifecycle_state} />
      )}
      {tab === "compliance" && (
        <ComplianceTab compliance={compliance} shipment={shipment} onEditThresholds={() => setShowEditDialog(true)} />
      )}
      {tab === "audit" && (
        <ShipmentAuditTimeline shipmentId={shipmentId} />
      )}

      {/* Sign dialog (lightweight) */}
      {showSignDialog && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" onClick={() => setShowSignDialog(false)}>
          <Card className="bg-zinc-950 border-zinc-800 p-6 w-[400px]" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-sm font-semibold text-zinc-100 mb-1">E-Signature</h3>
            <p className="text-[11px] text-zinc-500 mb-4">By typing your name below, you confirm receipt of this shipment in compliance with cold-chain protocols.</p>
            <Label className="text-[10px] uppercase tracking-wider text-zinc-400">Full name</Label>
            <Input value={signName} onChange={(e) => setSignName(e.target.value)} autoFocus
              className="bg-zinc-900 border-zinc-800 text-sm mt-1 font-mono"
              data-testid="sign-name-input"
              placeholder="Type your name" />
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={() => setShowSignDialog(false)} className="border-zinc-700 text-xs h-8">Cancel</Button>
              <Button onClick={handleSign} disabled={acting || !signName.trim()}
                className="bg-teal-500 hover:bg-teal-600 text-zinc-950 text-xs h-8" data-testid="sign-confirm-btn">
                Sign Shipment
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Corrective Action dialog */}
      {showCorrectiveDialog && (
        <CorrectiveActionDialog
          shipmentId={shipmentId}
          onClose={() => setShowCorrectiveDialog(false)}
          onLogged={() => { setShowCorrectiveDialog(false); fetchAll(); }}
        />
      )}

      {/* Workflow Status Change dialog */}
      {statusDialogOpen && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50"
          onClick={() => { setStatusDialogOpen(null); setStatusReason(""); }}>
          <Card className="bg-zinc-950 border-zinc-800 p-6 w-[460px]" onClick={(e) => e.stopPropagation()}
            data-testid="status-dialog">
            <h3 className="text-sm font-semibold text-zinc-100 mb-1">
              {statusDialogOpen === "quarantined" && "Quarantine Shipment"}
              {statusDialogOpen === "excursion_review" && "Mark Under Excursion Review"}
              {statusDialogOpen === "delivered" && "Release from Quarantine"}
            </h3>
            <p className="text-[11px] text-zinc-500 mb-4">
              {statusDialogOpen === "quarantined" && "Cargo will be held pending QA decision. This will appear on the compliance PDF status badge."}
              {statusDialogOpen === "excursion_review" && "Flag this shipment for compliance review without halting it. QA can investigate and release."}
              {statusDialogOpen === "delivered" && "Return status to delivered. Reason will be appended to the audit trail."}
            </p>
            <Label className="text-[10px] uppercase tracking-wider text-zinc-400">Reason / context (optional)</Label>
            <textarea value={statusReason} onChange={(e) => setStatusReason(e.target.value)}
              rows={3} maxLength={500}
              placeholder="e.g. 24-min excursion at 16:55, pending QA decision"
              className="w-full mt-1 bg-zinc-900 border border-zinc-800 rounded-md text-sm text-zinc-100 px-2.5 py-2 resize-none focus:outline-none focus:ring-1 focus:ring-teal-500"
              data-testid="status-reason" />
            <div className="flex justify-end gap-2 mt-5">
              <Button variant="outline" onClick={() => { setStatusDialogOpen(null); setStatusReason(""); }}
                className="border-zinc-700 text-xs h-8">Cancel</Button>
              <Button onClick={submitStatusChange} disabled={acting}
                className={`text-xs h-8 font-semibold text-zinc-950 ${
                  statusDialogOpen === "quarantined" ? "bg-red-500 hover:bg-red-600"
                    : statusDialogOpen === "excursion_review" ? "bg-amber-500 hover:bg-amber-600"
                    : "bg-emerald-500 hover:bg-emerald-600"
                }`}
                data-testid="status-confirm-btn">
                Confirm
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Edit Shipment dialog */}
      {showEditDialog && (
        <EditShipmentDialog
          shipment={shipment}
          onClose={() => setShowEditDialog(false)}
          onSaved={() => { setShowEditDialog(false); fetchAll(); toast.success("Shipment updated"); }}
        />
      )}

      {/* Shipment-scoped Logger Import dialog (Phase 53) */}
      {showImportLogger && (
        <ShipmentLoggerImportDialog
          shipment={shipment}
          onClose={() => setShowImportLogger(false)}
          onAttached={() => { setShowImportLogger(false); fetchAll(); }}
        />
      )}

      {/* Archive Shipment dialog */}
      {showArchiveDialog && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50"
          onClick={() => !archiving && setShowArchiveDialog(false)}>
          <Card className="bg-zinc-950 border-zinc-800 p-6 w-[460px]" onClick={(e) => e.stopPropagation()}
            data-testid="archive-dialog">
            <h3 className="text-sm font-semibold text-zinc-100 mb-1 flex items-center gap-2">
              <TrashSimple size={14} className="text-red-400" />
              Archive Shipment
            </h3>
            <p className="text-[11px] text-zinc-500 mb-4">
              The shipment <span className="font-mono text-zinc-300">{shipment.name}</span> will be
              hidden from operators, dashboards, search, and reports. <strong>All audit trail,
              logger uploads, evidence packages, alerts, and corrective actions are retained.</strong>
              {" "}A Super Admin can restore or permanently purge it later.
            </p>
            <div className="flex justify-end gap-2 mt-5">
              <Button variant="outline" disabled={archiving}
                onClick={() => setShowArchiveDialog(false)}
                className="border-zinc-700 text-xs h-8" data-testid="archive-cancel-btn">
                Cancel
              </Button>
              <Button disabled={archiving}
                onClick={async () => {
                  setArchiving(true);
                  try {
                    await axios.delete(`${API}/shipments/${shipmentId}`);
                    toast.success("Shipment archived");
                    setShowArchiveDialog(false);
                    if (onBack) onBack();
                  } catch (e) {
                    toast.error(e.response?.data?.detail || "Archive failed");
                  } finally {
                    setArchiving(false);
                  }
                }}
                className="bg-red-500 hover:bg-red-600 text-zinc-950 text-xs h-8 font-semibold"
                data-testid="archive-confirm-btn">
                {archiving ? "Archiving…" : "Archive Shipment"}
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}

function KPI({ label, value, color = "text-zinc-100" }) {
  return (
    <div>
      <p className="text-[9px] uppercase tracking-wider text-zinc-500">{label}</p>
      <p className={`text-sm font-mono font-semibold mt-0.5 ${color}`}>{value}</p>
    </div>
  );
}

const CORRECTIVE_ACTION_OPTIONS = [
  { value: "driver_inspection",        label: "Driver Inspected Reefer Unit" },
  { value: "cooling_adjusted",         label: "Cooling System Adjusted" },
  { value: "packaging_checked",        label: "Packaging Checked" },
  { value: "shipment_transferred",     label: "Shipment Transferred" },
  { value: "manual_temp_verification", label: "Manual Temperature Verification" },
  { value: "reefer_serviced",          label: "Reefer Unit Serviced" },
  { value: "container_resealed",       label: "Container Re-Sealed" },
  { value: "corrective_action",        label: "Other Corrective Action" },
];

function CorrectiveActionDialog({ shipmentId, onClose, onLogged }) {
  const [actionType, setActionType] = useState("driver_inspection");
  const [note, setNote] = useState("");
  const [performedBy, setPerformedBy] = useState("");
  const [submitting, setSubmitting] = useState(false);
  // Phase 21: ownership
  const [mode, setMode] = useState("performed"); // "performed" | "assignment"
  const [assigneeRole, setAssigneeRole] = useState("qa_manager");
  const [assigneeQuery, setAssigneeQuery] = useState("");
  const [assignee, setAssignee] = useState(null);
  const [dueByHours, setDueByHours] = useState(48);
  const { results: assigneeResults } = usePartySearch(assigneeQuery);

  const submit = async () => {
    setSubmitting(true);
    try {
      const dueIso = mode === "assignment" && dueByHours > 0
        ? new Date(Date.now() + dueByHours * 3600 * 1000).toISOString()
        : undefined;
      await axios.post(`${API}/shipments/${shipmentId}/corrective-actions`, {
        action_type: actionType,
        note: note.trim() || undefined,
        performed_by: performedBy.trim() || undefined,
        is_assignment: mode === "assignment",
        assigned_to_role: mode === "assignment" ? assigneeRole : undefined,
        assigned_to_party_type: mode === "assignment" && assignee ? assignee.party_type : undefined,
        assigned_to_party_id: mode === "assignment" && assignee ? assignee.party_id : undefined,
        due_by: dueIso,
      });
      toast.success(mode === "assignment" ? "Corrective action assigned" : "Corrective action logged");
      onLogged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to log action");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" onClick={onClose}>
      <Card className="bg-zinc-950 border-zinc-800 p-6 w-[520px] max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="corrective-dialog">
        <div className="flex items-center gap-2 mb-1">
          <Wrench size={14} className="text-amber-400" weight="duotone" />
          <h3 className="text-sm font-semibold text-zinc-100">Corrective Action</h3>
        </div>
        <p className="text-[11px] text-zinc-500 mb-3">
          Record completed work, or <span className="text-amber-300">assign</span> an action to a responsible party with a due date.
        </p>

        {/* Mode toggle */}
        <div className="flex items-center gap-1 p-0.5 bg-zinc-900 rounded border border-zinc-800 mb-4">
          {[
            { v: "performed", label: "Performed" },
            { v: "assignment", label: "Assign Owner" },
          ].map((m) => (
            <button key={m.v} onClick={() => setMode(m.v)} type="button"
              className={`flex-1 text-xs h-7 rounded ${mode === m.v ? "bg-amber-500/15 text-amber-300 font-semibold" : "text-zinc-500"}`}
              data-testid={`mode-${m.v}`}>
              {m.label}
            </button>
          ))}
        </div>

        <Label className="text-[10px] uppercase tracking-wider text-zinc-400">Action type</Label>
        <select value={actionType} onChange={(e) => setActionType(e.target.value)}
          className="w-full mt-1 bg-zinc-900 border border-zinc-800 rounded-md text-sm text-zinc-100 px-2.5 py-2 focus:outline-none focus:ring-1 focus:ring-amber-500"
          data-testid="corrective-action-type">
          {CORRECTIVE_ACTION_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>

        {mode === "assignment" ? (
          <>
            <div className="grid grid-cols-2 gap-3 mt-3">
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-zinc-400">Assignee role</Label>
                <select value={assigneeRole} onChange={(e) => setAssigneeRole(e.target.value)}
                  className="w-full mt-1 bg-zinc-900 border border-zinc-800 rounded text-sm text-zinc-100 px-2.5 py-2 h-9">
                  {SHIPMENT_PARTY_ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-zinc-400">Due in (hours)</Label>
                <Input type="number" min={1} value={dueByHours}
                  onChange={(e) => setDueByHours(parseInt(e.target.value) || 0)}
                  className="bg-zinc-900 border-zinc-800 text-sm h-9 mt-1 font-mono" />
              </div>
            </div>
            <Label className="text-[10px] uppercase tracking-wider text-zinc-400 mt-3 block">Assign to (optional — overrides role-resolution)</Label>
            <Input value={assigneeQuery} onChange={(e) => { setAssigneeQuery(e.target.value); setAssignee(null); }}
              placeholder="Search contacts / facilities / orgs…"
              className="bg-zinc-900 border-zinc-800 text-sm mt-1" />
            {assignee ? (
              <div className="mt-2 p-2 rounded border border-amber-700/50 bg-amber-500/5">
                <div className="text-[10px] uppercase tracking-wider text-amber-400">{assignee.party_type}</div>
                <div className="text-sm text-zinc-100">{assignee.label}</div>
                {assignee.sub_label && <div className="text-[10px] text-zinc-500">{assignee.sub_label}</div>}
                <button onClick={() => setAssignee(null)} className="text-[10px] text-zinc-500 hover:text-zinc-300 mt-0.5">change</button>
              </div>
            ) : (assigneeResults.length > 0 && (
              <div className="mt-1 max-h-32 overflow-y-auto border border-zinc-800 rounded bg-zinc-900/40">
                {assigneeResults.map((r) => (
                  <button key={`${r.party_type}-${r.party_id}`} onClick={() => setAssignee(r)}
                    className="w-full text-left px-2.5 py-1.5 hover:bg-zinc-800/50 border-b border-zinc-800 last:border-0">
                    <div className="text-[9px] uppercase text-amber-400">{r.party_type}</div>
                    <div className="text-sm text-zinc-100">{r.label}</div>
                    {r.sub_label && <div className="text-[10px] text-zinc-500">{r.sub_label}</div>}
                  </button>
                ))}
              </div>
            ))}
          </>
        ) : (
          <>
            <Label className="text-[10px] uppercase tracking-wider text-zinc-400 mt-3 block">Performed by (optional)</Label>
            <Input value={performedBy} onChange={(e) => setPerformedBy(e.target.value)}
              className="bg-zinc-900 border-zinc-800 text-sm mt-1" data-testid="corrective-performed-by"
              placeholder="Operator name" />
          </>
        )}

        <Label className="text-[10px] uppercase tracking-wider text-zinc-400 mt-3 block">Note (optional)</Label>
        <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} maxLength={500}
          className="w-full mt-1 bg-zinc-900 border border-zinc-800 rounded-md text-sm text-zinc-100 px-2.5 py-2 focus:outline-none focus:ring-1 focus:ring-amber-500 resize-none"
          data-testid="corrective-note"
          placeholder="Details for the audit record (max 500 chars)" />

        <div className="flex justify-end gap-2 mt-5">
          <Button variant="outline" onClick={onClose} className="border-zinc-700 text-xs h-8">Cancel</Button>
          <Button onClick={submit} disabled={submitting}
            className="bg-amber-500 hover:bg-amber-600 text-zinc-950 text-xs h-8 font-semibold"
            data-testid="corrective-submit-btn">
            <Wrench size={11} className="mr-1" /> {mode === "assignment" ? "Assign" : "Log Action"}
          </Button>
        </div>
      </Card>
    </div>
  );
}

function OverviewTab({ shipment, compliance, shipmentRisk, driverRisk, onSelectDevice, onReload }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      {/* Devices */}
      <Card className="bg-zinc-900 border-zinc-800 p-5 lg:col-span-2">
        <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-400 mb-3 flex items-center gap-1.5">
          <Package size={11} /> Devices
        </h3>
        {(shipment.device_names || []).length === 0 ? (
          <p className="text-xs text-zinc-600">No devices assigned</p>
        ) : (
          <div className="space-y-1.5">
            {shipment.device_names.map((name) => {
              const recent = (shipment.recent_events || []).find((e) => e.device_name === name);
              return (
                <div key={name}
                  onClick={() => recent?.dataset_id && onSelectDevice?.(name, recent.dataset_id)}
                  className={`flex items-center gap-3 p-2 rounded-sm bg-zinc-950/50 ${recent?.dataset_id ? "hover:bg-zinc-800 cursor-pointer" : ""}`}
                  data-testid={`ship-device-${name}`}>
                  <Package size={14} className="text-teal-400" />
                  <span className="text-xs font-mono font-semibold text-zinc-200 flex-1">{name}</span>
                  {recent && (
                    <>
                      <span className="text-[10px] font-mono text-zinc-400">{recent.temperature}°F</span>
                      <span className="text-[10px] font-mono text-zinc-500">{recent.humidity}%</span>
                      <span className={`text-[10px] font-mono ${(recent.light || "").toLowerCase() !== "dark" ? "text-amber-400" : "text-zinc-600"}`}>
                        {recent.light || "—"}
                      </span>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* Quick stats */}
      <Card className="bg-zinc-900 border-zinc-800 p-5">
        <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-400 mb-3 flex items-center gap-1.5">
          <ShieldCheck size={11} /> Quick Stats
        </h3>
        {compliance?.summary ? (
          <div className="space-y-3 text-xs">
            <Row icon={Thermometer} label="Avg Temp" value={compliance.summary.temp_stats.avg ? `${compliance.summary.temp_stats.avg}°F` : "—"} />
            <Row icon={Thermometer} label="Min/Max"
              value={compliance.summary.temp_stats.min != null ? `${compliance.summary.temp_stats.min}–${compliance.summary.temp_stats.max}°F` : "—"} />
            <Row icon={Warning} label="Excursions" value={compliance.summary.temp_excursions}
              color={compliance.summary.temp_excursions > 0 ? "text-amber-400" : "text-emerald-400"} />
            <Row icon={Lightbulb} label="Seal Breaches" value={compliance.summary.seal_breaches}
              color={compliance.summary.seal_breaches > 0 ? "text-amber-400" : "text-emerald-400"} />
            <Row icon={Warning} label="Total Alerts" value={compliance.summary.total_alerts}
              color={compliance.summary.total_alerts > 0 ? "text-red-400" : "text-emerald-400"} />
          </div>
        ) : (
          <p className="text-[11px] text-zinc-600">Compliance data will appear once devices report.</p>
        )}
      </Card>

      {/* Geofences & Devices (Registry) */}
      <Card className="bg-zinc-900 border-zinc-800 p-5 lg:col-span-3">
        <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-400 mb-4">
          Geofences & Devices
        </h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <GeofenceAssignmentsEditor
            shipmentId={shipment.id}
            initialAssignments={shipment.geofence_assignments || []}
            onAfterChange={() => onReload?.()}
          />
          <DeviceAssignmentsEditor
            shipmentId={shipment.id}
            onAfterChange={() => onReload?.()}
          />
        </div>
      </Card>

      {/* Risk cards spanning full width */}
      <div className="lg:col-span-3 grid grid-cols-1 lg:grid-cols-2 gap-4">
        <RiskCard title="Shipment Risk" icon={Truck} payload={shipmentRisk}
          testid="shipment-risk-card" />
        {shipment.driver_name && (
          <RiskCard title={`Driver Risk · ${shipment.driver_name}`} icon={Steering} payload={driverRisk}
            testid="driver-risk-card"
            footer={driverRisk?.stats && (
              <div className="pt-3 border-t border-zinc-800 grid grid-cols-3 gap-2 text-center">
                <DriverStat label="Trips" value={driverRisk.stats.total_shipments} />
                <DriverStat label="Avg Compliance"
                  value={driverRisk.stats.avg_compliance != null ? `${driverRisk.stats.avg_compliance}%` : "—"}
                  color={(driverRisk.stats.avg_compliance ?? 100) >= 95 ? "text-emerald-400" :
                         (driverRisk.stats.avg_compliance ?? 0) >= 85 ? "text-amber-400" : "text-red-400"} />
                <DriverStat label="Excursions"
                  value={`${driverRisk.stats.excursion_pct}%`}
                  color={driverRisk.stats.excursion_pct > 30 ? "text-red-400" : driverRisk.stats.excursion_pct > 10 ? "text-amber-400" : "text-emerald-400"} />
              </div>
            )} />
        )}
      </div>
    </div>
  );
}

function DriverStat({ label, value, color = "text-zinc-200" }) {
  return (
    <div>
      <p className={`text-sm font-mono font-bold ${color}`}>{value}</p>
      <p className="text-[9px] uppercase tracking-wider text-zinc-500 mt-0.5">{label}</p>
    </div>
  );
}

function ComplianceTab({ compliance, shipment, onEditThresholds }) {
  const [exporting, setExporting] = useState(false);
  const [lastExport, setLastExport] = useState(null);

  // Pre-populate "last generated" from audit trail if present
  useEffect(() => {
    const trail = (shipment?.audit_trail || []).filter(e => e.action === "compliance_pdf_exported");
    if (trail.length) {
      const latest = trail[trail.length - 1];
      setLastExport({ at: latest.timestamp, by: latest.user_email, report_id: latest.details?.report_id });
    }
  }, [shipment]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/reports/shipments/${shipment.id}/compliance-pdf`, {
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const fname = res.headers["content-disposition"]?.match(/filename=([^;]+)/)?.[1] || `shipment_${shipment.id.slice(0,8)}.pdf`;
      a.download = fname.trim();
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setLastExport({
        at: new Date().toISOString(),
        by: "you",
        report_id: res.headers["x-report-id"],
      });
      toast.success("Compliance PDF generated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Export failed");
    } finally {
      setExporting(false);
    }
  };

  if (!compliance || compliance.compliance_score == null) {
    return <Card className="bg-zinc-900 border-zinc-800 p-12 text-center">
      <ShieldCheck size={32} weight="duotone" className="text-zinc-700 mx-auto mb-2" />
      <p className="text-sm text-zinc-400">No compliance data yet</p>
      <p className="text-xs text-zinc-600 mt-1">Assign devices and start the shipment to begin recording.</p>
    </Card>;
  }

  const sum = compliance.summary;
  const score = compliance.compliance_score;
  // Red / Amber / Green tier — matches the per-shipment KPI thresholds used
  // elsewhere in the app (Accountability, Shipments list).
  const tier =
    score >= 95 ? { color: "text-emerald-400", stroke: "stroke-emerald-400", bg: "bg-emerald-500/10", label: "PASS" } :
    score >= 80 ? { color: "text-amber-400",   stroke: "stroke-amber-400",   bg: "bg-amber-500/10",   label: "AT RISK" } :
                  { color: "text-red-400",     stroke: "stroke-red-400",     bg: "bg-red-500/10",     label: "FAIL" };
  const tMin = compliance.shipment.temp_range.min;
  const tMax = compliance.shipment.temp_range.max;
  const nonCompliant = (sum.total_readings || 0) - (sum.compliant_readings || 0);
  // Ring geometry — SVG circle stroke-dashoffset.
  const RING_RADIUS = 38;
  const RING_C = 2 * Math.PI * RING_RADIUS;
  const ringOffset = RING_C * (1 - (score || 0) / 100);

  return (
    <div className="space-y-4">
      {/* Export bar */}
      <Card className="bg-gradient-to-r from-teal-500/5 to-zinc-900 border-zinc-800 p-4 flex items-center justify-between flex-wrap gap-3"
        data-testid="compliance-export-bar">
        <div>
          <h3 className="text-sm font-semibold text-zinc-100 flex items-center gap-2">
            <FileText size={14} weight="duotone" className="text-teal-400" />
            Audit-Ready Compliance PDF
          </h3>
          <p className="text-[11px] text-zinc-500 mt-0.5">
            Includes summary, timeline, route, devices, AI risk, audit trail, and SHA-256 checksum.
          </p>
          {lastExport && (
            <p className="text-[10px] font-mono text-zinc-600 mt-1" data-testid="last-export-info">
              Last generated {new Date(lastExport.at).toLocaleString()} by {lastExport.by}
              {lastExport.report_id && <span className="ml-1 text-zinc-700">· #{lastExport.report_id.slice(0, 8)}</span>}
            </p>
          )}
        </div>
        <Button onClick={handleExport} disabled={exporting}
          className="bg-teal-500 hover:bg-teal-600 text-zinc-950 text-xs h-9 font-semibold"
          data-testid="export-compliance-pdf-btn">
          <FileText size={12} className="mr-1.5" />
          {exporting ? "Generating..." : "Export Compliance PDF"}
        </Button>
      </Card>

      {/* Hero KPI — Temperature Compliance Score */}
      <Card className="bg-zinc-900 border-zinc-800 p-5" data-testid="compliance-hero">
        <div className="flex items-start gap-5 flex-wrap">
          {/* Circular ring */}
          <div className="relative shrink-0">
            <svg width="120" height="120" viewBox="0 0 100 100" className="-rotate-90"
              data-testid="compliance-score-ring">
              <circle cx="50" cy="50" r={RING_RADIUS} className="stroke-zinc-800" strokeWidth="9" fill="none" />
              <circle cx="50" cy="50" r={RING_RADIUS}
                className={`${tier.stroke} transition-[stroke-dashoffset] duration-700 ease-out`}
                strokeWidth="9" strokeLinecap="round" fill="none"
                strokeDasharray={RING_C} strokeDashoffset={ringOffset} />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className={`text-2xl font-mono font-bold ${tier.color}`} data-testid="compliance-score">{score}%</span>
              <span className={`text-[9px] font-bold tracking-wider uppercase mt-0.5 ${tier.color}`}>{tier.label}</span>
            </div>
          </div>

          {/* Label, helper, headline */}
          <div className="flex-1 min-w-[240px]">
            <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-400 mb-1 flex items-center gap-2">
              Temperature Compliance Score
              <span className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${tier.bg} ${tier.color}`}
                data-testid="compliance-tier">{tier.label}</span>
            </h3>
            <p className="text-[11px] text-zinc-500 leading-relaxed">
              Score is calculated from readings within the configured shipment threshold.
              {" "}
              <button onClick={onEditThresholds}
                className="text-teal-400 hover:text-teal-300 underline underline-offset-2"
                data-testid="compliance-edit-thresholds">
                Adjust thresholds
              </button>
              {" "}if the default does not match this cargo type.
            </p>
            <p className={`text-sm font-mono mt-2 ${nonCompliant === 0 ? "text-emerald-300" : "text-zinc-200"}`}
               data-testid="compliance-headline">
              <span className="font-bold">{sum.compliant_readings}</span> of{" "}
              <span className="font-bold">{sum.total_readings}</span> readings were within{" "}
              <span className="font-bold">{tMin}–{tMax}°F</span>.
            </p>
          </div>

          {/* Side stats — Excursions / Seal / Alerts */}
          <div className="grid grid-cols-3 gap-3 text-center shrink-0">
            <BigStat label="Excursions" value={sum.temp_excursions} color={sum.temp_excursions > 0 ? "text-amber-400" : "text-emerald-400"} />
            <BigStat label="Seal Breaches" value={sum.seal_breaches} color={sum.seal_breaches > 0 ? "text-amber-400" : "text-emerald-400"} />
            <BigStat label="Alerts" value={sum.total_alerts} color={sum.total_alerts > 0 ? "text-red-400" : "text-emerald-400"} />
          </div>
        </div>

        {/* Sub-stats grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mt-5 pt-4 border-t border-zinc-800"
             data-testid="compliance-substats">
          <CompStatTile label="Total readings" value={sum.total_readings} mono />
          <CompStatTile label="Compliant"      value={sum.compliant_readings} mono accent="text-emerald-300" />
          <CompStatTile label="Non-compliant"  value={nonCompliant} mono accent={nonCompliant > 0 ? "text-red-300" : "text-emerald-300"} />
          <CompStatTile label="Threshold"      value={`${tMin}–${tMax}°F`} mono accent="text-teal-300" />
          <CompStatTile label="Avg temp"
                        value={sum.temp_stats?.avg != null ? `${sum.temp_stats.avg}°F` : "—"} mono />
          <CompStatTile label="Peak temp"
                        value={sum.temp_stats?.max != null ? `${sum.temp_stats.max}°F` : "—"} mono
                        accent={(sum.temp_stats?.max ?? 0) > tMax ? "text-red-300" : "text-emerald-300"} />
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Excursion timeline */}
        <Card className="bg-zinc-900 border-zinc-800 p-5">
          <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-400 mb-3 flex items-center gap-1.5">
            <Thermometer size={11} className="text-amber-400" /> Excursion Timeline
          </h3>
          <ScrollArea className="h-[260px]">
            {compliance.excursion_timeline.length === 0 ? (
              <p className="text-xs text-emerald-400">No excursions detected</p>
            ) : (
              <div className="space-y-1.5">
                {compliance.excursion_timeline.map((e, i) => (
                  <div key={i} className="flex items-center gap-2 p-2 rounded-sm bg-zinc-950/50 border-l-2 border-amber-500" data-testid={`excursion-${i}`}>
                    <Thermometer size={12} className={e.type === "above_max" ? "text-red-400" : "text-blue-400"} />
                    <div className="flex-1 min-w-0">
                      <p className="text-[11px] font-mono text-zinc-200">
                        <span className={e.type === "above_max" ? "text-red-400" : "text-blue-400"}>{e.temperature}°F</span>
                        <span className="text-zinc-500"> · {e.deviation}°F {e.type === "above_max" ? "above max" : "below min"}</span>
                      </p>
                      <p className="text-[9px] font-mono text-zinc-600">{e.device_name} · {(e.timestamp || "").substring(0, 19)}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </Card>

        {/* Seal breaches */}
        <Card className="bg-zinc-900 border-zinc-800 p-5">
          <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-400 mb-3 flex items-center gap-1.5">
            <Lightbulb size={11} className="text-amber-400" /> Seal Integrity
          </h3>
          <ScrollArea className="h-[260px]">
            {compliance.seal_breaches.length === 0 ? (
              <p className="text-xs text-emerald-400">Seal intact — no light detected</p>
            ) : (
              <div className="space-y-1.5">
                {compliance.seal_breaches.map((b, i) => (
                  <div key={i} className="flex items-center gap-2 p-2 rounded-sm bg-zinc-950/50 border-l-2 border-amber-500" data-testid={`seal-breach-${i}`}>
                    <Lightbulb size={12} className="text-amber-400" />
                    <div className="flex-1 min-w-0">
                      <p className="text-[11px] font-mono text-zinc-200">{b.value}</p>
                      <p className="text-[9px] font-mono text-zinc-600">{b.device_name} · {(b.timestamp || "").substring(0, 19)}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </Card>
      </div>

      {/* Signature block */}
      {(shipment.signature?.required || shipment.signature?.signed_by) && (
        <Card className="bg-zinc-900 border-zinc-800 p-5">
          <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-400 mb-3 flex items-center gap-1.5">
            <PenNib size={11} /> E-Signature
          </h3>
          {shipment.signature.signed_by ? (
            <div className="border-l-2 border-emerald-500 pl-4 py-1">
              <p className="font-mono text-base text-zinc-100" style={{ fontFamily: "cursive" }}>{shipment.signature.signature_text}</p>
              <p className="text-[10px] font-mono text-zinc-500 mt-1">
                Signed by <span className="text-zinc-300">{shipment.signature.signed_by}</span> ·
                {(shipment.signature.signed_at || "").substring(0, 19)}
              </p>
            </div>
          ) : (
            <p className="text-[11px] text-zinc-500 italic">Signature pending. Mark delivered, then capture e-signature.</p>
          )}
        </Card>
      )}
    </div>
  );
}

function AuditTab({ audit, signature }) {
  const events = [...audit];
  if (signature?.signed_by && !events.some(e => e.action === "signed")) {
    events.push({ action: "signed", user_email: signature.signed_by, timestamp: signature.signed_at });
  }
  events.sort((a, b) => (b.timestamp || "").localeCompare(a.timestamp || ""));

  return (
    <Card className="bg-zinc-900 border-zinc-800 p-5">
      <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-400 mb-4 flex items-center gap-1.5">
        <FileText size={11} /> Audit Trail
        <span className="ml-auto text-[10px] font-normal text-zinc-600 normal-case tracking-normal">{events.length} events</span>
      </h3>
      {events.length === 0 ? (
        <p className="text-xs text-zinc-600">No audit entries yet</p>
      ) : (
        <div className="relative pl-4 space-y-3" data-testid="audit-trail">
          <div className="absolute left-1 top-1 bottom-1 w-px bg-zinc-800" />
          {events.map((e, i) => (
            <div key={i} className="relative" data-testid={`audit-entry-${e.action}`}>
              <div className="absolute -left-3.5 top-1 w-2 h-2 rounded-full bg-teal-400 border-2 border-zinc-900" />
              <div className="flex items-baseline gap-2 flex-wrap">
                <span className="text-xs font-mono font-semibold text-zinc-200 capitalize">{e.action}</span>
                <span className="text-[10px] text-zinc-500">by {e.user_email}</span>
                <span className="text-[10px] font-mono text-zinc-600 ml-auto">{(e.timestamp || "").substring(0, 19).replace("T", " ")}</span>
              </div>
              {e.details && Object.keys(e.details).length > 0 && (
                <p className="text-[10px] font-mono text-zinc-500 mt-0.5">{JSON.stringify(e.details)}</p>
              )}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function Row({ icon: Icon, label, value, color = "text-zinc-200" }) {
  return (
    <div className="flex items-center justify-between">
      <span className="flex items-center gap-1.5 text-[11px] text-zinc-500"><Icon size={11} />{label}</span>
      <span className={`text-xs font-mono ${color}`}>{value}</span>
    </div>
  );
}

function BigStat({ label, value, color }) {
  return (
    <div>
      <p className={`text-2xl font-mono font-bold ${color}`}>{value}</p>
      <p className="text-[9px] uppercase tracking-wider text-zinc-500 mt-0.5">{label}</p>
    </div>
  );
}

function CompStatTile({ label, value, mono = false, accent = "text-zinc-200" }) {
  return (
    <div className="px-3 py-2 rounded-sm bg-zinc-950/50 border border-zinc-800"
         data-testid={`comp-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <p className="text-[9px] uppercase tracking-wider text-zinc-500 mb-0.5">{label}</p>
      <p className={`text-sm font-bold ${mono ? "font-mono" : ""} ${accent}`}>{value}</p>
    </div>
  );
}

// ============================================================================
// Phase B + C: Parties (shipment-scoped) & Chain-of-Custody Handoffs Tab
// ============================================================================

const SHIPMENT_PARTY_ROLES = [
  { value: "shipper", label: "Shipper" },
  { value: "consignee", label: "Consignee" },
  { value: "receiver", label: "Receiver" },
  { value: "carrier", label: "Carrier" },
  { value: "warehouse", label: "Warehouse" },
  { value: "qa_manager", label: "QA Manager" },
  { value: "compliance_contact", label: "Compliance Contact" },
  { value: "monitored_by", label: "Monitored By" },
  { value: "approved_by", label: "Approved By" },
];

const HANDOFF_ACTIONS = [
  { value: "released_by", label: "Released by" },
  { value: "received_by", label: "Received by" },
  { value: "inspected_by", label: "Inspected by" },
  { value: "approved_corrective_action_by", label: "Corrective action approved by" },
  { value: "custody_transferred", label: "Custody transferred to" },
  { value: "delivered_to", label: "Delivered to" },
  { value: "handoff_other", label: "Other handoff" },
];

function PartiesAndCustodyTab({ shipment, shipmentId, onUpdated }) {
  const [showAttach, setShowAttach] = useState(false);
  const [showHandoff, setShowHandoff] = useState(false);
  const [correctiveActions, setCorrectiveActions] = useState([]);
  const parties = shipment.parties || [];
  const handoffs = shipment.handoffs || [];

  const loadCAs = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/shipments/${shipmentId}/corrective-actions`);
      setCorrectiveActions(data.items || []);
    } catch { /* noop */ }
  }, [shipmentId]);
  useEffect(() => { loadCAs(); }, [loadCAs, shipment.audit_trail]);

  const updateCA = async (ca_id, op) => {
    try {
      await axios.patch(`${API}/shipments/${shipmentId}/corrective-actions/${ca_id}`, { op });
      toast.success(`Action ${op}d`);
      loadCAs(); onUpdated();
    } catch (e) {
      toast.error(e.response?.data?.detail || `${op} failed`);
    }
  };

  const detach = async (idx) => {
    if (!window.confirm("Remove this party from the shipment?")) return;
    const next = parties.filter((_, i) => i !== idx);
    try {
      await axios.put(`${API}/shipments/${shipmentId}`, { parties: next });
      toast.success("Party removed");
      onUpdated();
    } catch (e) {
      toast.error("Failed to remove party");
    }
  };

  const deleteHandoff = async (id) => {
    if (!window.confirm("Delete this handoff record?")) return;
    try {
      await axios.delete(`${API}/shipments/${shipmentId}/handoffs/${id}`);
      toast.success("Handoff removed");
      onUpdated();
    } catch (e) {
      toast.error("Failed to remove handoff");
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="parties-custody-tab">
      {/* Parties panel */}
      <Card className="bg-zinc-900/40 border-zinc-800 p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-sm font-semibold text-zinc-100 flex items-center gap-2">
              <Buildings size={14} className="text-teal-400" weight="duotone" />
              Shipment Parties
            </h3>
            <p className="text-[11px] text-zinc-500 mt-0.5">Operational stakeholders for this shipment</p>
          </div>
          <Button onClick={() => setShowAttach(true)} size="sm"
            className="bg-teal-500 hover:bg-teal-600 text-zinc-950 text-xs h-8 font-semibold"
            data-testid="attach-party-btn">
            + Attach Party
          </Button>
        </div>

        {parties.length === 0 ? (
          <p className="text-[11px] text-zinc-500 italic py-6 text-center">
            No structured parties attached. Click <span className="text-teal-400">Attach Party</span> to link
            registry entries by role (Shipper, Consignee, QA Manager, etc.).
          </p>
        ) : (
          <div className="space-y-2">
            {parties.map((p, i) => (
              <div key={i} className="flex items-center gap-3 p-2.5 rounded border border-zinc-800 bg-zinc-950/30 group">
                <div className="flex-1 min-w-0">
                  <div className="text-[9px] uppercase tracking-wider text-zinc-500">
                    {SHIPMENT_PARTY_ROLES.find(r => r.value === p.role)?.label || p.role}
                  </div>
                  <div className="text-sm font-medium text-zinc-100 truncate">{p.label || p.party_id}</div>
                  {p.sub_label && <div className="text-[10px] text-zinc-500 truncate">{p.sub_label}</div>}
                </div>
                <Button variant="ghost" size="sm" onClick={() => detach(i)}
                  className="h-7 w-7 p-0 text-zinc-500 hover:text-red-300 opacity-0 group-hover:opacity-100">
                  <TrashSimple size={11} />
                </Button>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Handoffs panel */}
      <Card className="bg-zinc-900/40 border-zinc-800 p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-sm font-semibold text-zinc-100 flex items-center gap-2">
              <Hash size={14} className="text-amber-400" weight="duotone" />
              Chain-of-Custody Handoffs
            </h3>
            <p className="text-[11px] text-zinc-500 mt-0.5">Operational accountability log</p>
          </div>
          <Button onClick={() => setShowHandoff(true)} size="sm"
            className="bg-amber-500 hover:bg-amber-600 text-zinc-950 text-xs h-8 font-semibold"
            data-testid="record-handoff-btn">
            + Record Handoff
          </Button>
        </div>

        {handoffs.length === 0 ? (
          <p className="text-[11px] text-zinc-500 italic py-6 text-center">
            No handoffs recorded yet. Use <span className="text-amber-400">Record Handoff</span> to log
            release/receive/inspection events with structured party references.
          </p>
        ) : (
          <div className="space-y-2">
            {handoffs.map((h) => (
              <div key={h.id} className="p-2.5 rounded border border-zinc-800 bg-zinc-950/30 group">
                <div className="flex items-baseline justify-between gap-2">
                  <span className="text-[10px] uppercase tracking-wider text-amber-400 font-semibold">
                    {HANDOFF_ACTIONS.find(a => a.value === h.action)?.label || h.action}
                  </span>
                  <span className="text-[10px] font-mono text-zinc-500">
                    {h.timestamp ? new Date(h.timestamp).toUTCString().slice(5, 22) : "—"}
                  </span>
                </div>
                <div className="text-sm font-medium text-zinc-100 mt-0.5">
                  {h.person_name || h.facility_name || h.organization_name || "—"}
                </div>
                <div className="text-[10px] text-zinc-500">
                  {[h.role?.replace(/_/g, " "), h.facility_name, h.organization_name].filter(Boolean).join(" · ")}
                </div>
                {h.note && <p className="text-[11px] text-zinc-400 mt-1 italic">{h.note}</p>}
                <div className="flex justify-end mt-1">
                  <Button variant="ghost" size="sm" onClick={() => deleteHandoff(h.id)}
                    className="h-6 px-2 text-[10px] text-zinc-500 hover:text-red-300 opacity-0 group-hover:opacity-100">
                    Remove
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Corrective Actions panel — spans both columns */}
      <Card className="bg-zinc-900/40 border-zinc-800 p-5 lg:col-span-2" data-testid="corrective-actions-panel">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-sm font-semibold text-zinc-100 flex items-center gap-2">
              <Wrench size={14} className="text-amber-400" weight="duotone" />
              Open Corrective Actions
              {correctiveActions.filter(c => !["completed", "cancelled"].includes(c.status)).length > 0 && (
                <Badge className="bg-amber-500/15 text-amber-300 border-amber-700/40 text-[10px] h-5 font-mono"
                  data-testid="open-ca-count">
                  {correctiveActions.filter(c => !["completed", "cancelled"].includes(c.status)).length} open
                </Badge>
              )}
            </h3>
            <p className="text-[11px] text-zinc-500 mt-0.5">
              Assigned ownership · acknowledgement · resolution trail (chain-of-custody)
            </p>
          </div>
        </div>

        {correctiveActions.length === 0 ? (
          <p className="text-[11px] text-zinc-500 italic py-6 text-center">
            No corrective actions logged yet. Use the <span className="text-amber-300">Log Action</span> button
            in the header to assign or record one.
          </p>
        ) : (
          <div className="space-y-2">
            {correctiveActions.map((ca) => (
              <CorrectiveActionRow key={ca.id} ca={ca} onUpdate={updateCA} />
            ))}
          </div>
        )}
      </Card>

      {showAttach && (
        <AttachPartyDialog
          existing={parties}
          onClose={() => setShowAttach(false)}
          onAttached={async (newList) => {
            try {
              await axios.put(`${API}/shipments/${shipmentId}`, { parties: newList });
              toast.success("Party attached");
              setShowAttach(false);
              onUpdated();
            } catch (e) {
              toast.error("Attach failed");
            }
          }}
        />
      )}
      {showHandoff && (
        <RecordHandoffDialog
          shipmentId={shipmentId}
          onClose={() => setShowHandoff(false)}
          onRecorded={() => { setShowHandoff(false); onUpdated(); }}
        />
      )}
    </div>
  );
}

// Single open/resolved corrective action row with lifecycle controls
function CorrectiveActionRow({ ca, onUpdate }) {
  const isClosed = ["completed", "cancelled"].includes(ca.status);
  const isLegacy = ca._legacy === true;
  const actionLabel = (CORRECTIVE_ACTION_OPTIONS.find(o => o.value === ca.action_type)?.label) || ca.action_type;
  const statusStyle = {
    open:         { bg: "bg-amber-500/15",   text: "text-amber-300",   label: "OPEN" },
    acknowledged: { bg: "bg-sky-500/15",     text: "text-sky-300",     label: "ACKNOWLEDGED" },
    escalated:    { bg: "bg-red-500/15",     text: "text-red-300",     label: "ESCALATED" },
    completed:    { bg: "bg-emerald-500/15", text: "text-emerald-300", label: "COMPLETED" },
    cancelled:    { bg: "bg-zinc-500/15",    text: "text-zinc-300",    label: "CANCELLED" },
  }[ca.status] || { bg: "bg-zinc-500/15", text: "text-zinc-300", label: (ca.status || "—").toUpperCase() };

  // due-by countdown for open items
  let dueBadge = null;
  if (ca.due_by && !isClosed) {
    const dueMs = new Date(ca.due_by).getTime();
    const diffH = Math.round((dueMs - Date.now()) / 3600000);
    const overdue = diffH < 0;
    dueBadge = (
      <span className={`text-[10px] font-mono px-2 py-0.5 rounded border ${
        overdue ? "border-red-700/50 bg-red-500/10 text-red-300" : "border-amber-700/50 bg-amber-500/5 text-amber-300"
      }`}>
        {overdue ? `${Math.abs(diffH)}h overdue` : `due in ${diffH}h`}
      </span>
    );
  }

  return (
    <div className={`p-3 rounded border ${isClosed ? "border-zinc-800 bg-zinc-950/30" : "border-amber-700/30 bg-amber-500/5"}`}
      data-testid={`ca-row-${ca.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-[9px] uppercase tracking-wider font-bold px-2 py-0.5 rounded ${statusStyle.bg} ${statusStyle.text}`}>
              {statusStyle.label}
            </span>
            <span className="text-sm font-medium text-zinc-100 truncate">{actionLabel}</span>
            {dueBadge}
            {isLegacy && (
              <span className="text-[9px] uppercase text-zinc-500 border border-zinc-700 rounded px-1.5">legacy</span>
            )}
          </div>

          {/* Ownership stack */}
          {(ca.assigned_to_label || ca.assigned_to_role) && (
            <div className="mt-1.5 text-[11px]">
              <span className="text-zinc-500">Owner: </span>
              <span className="text-zinc-200 font-medium">{ca.assigned_to_label || "—"}</span>
              {ca.assigned_to_role && (
                <span className="text-zinc-500"> · {ca.assigned_to_role.replace(/_/g, " ")}</span>
              )}
              {ca.assigned_to_sub_label && (
                <span className="text-zinc-500"> · {ca.assigned_to_sub_label}</span>
              )}
            </div>
          )}
          {ca.performed_by && (
            <div className="text-[11px] text-zinc-500 mt-0.5">
              Performed by <span className="text-zinc-300">{ca.performed_by}</span>
            </div>
          )}
          {ca.note && (
            <p className="text-[11px] text-zinc-400 italic mt-1">{ca.note}</p>
          )}

          {/* Timestamps */}
          <div className="flex flex-wrap gap-3 mt-2 text-[10px] text-zinc-500 font-mono">
            {ca.created_at && <span>created {new Date(ca.created_at).toUTCString().slice(5, 22)}</span>}
            {ca.acknowledged_at && <span className="text-sky-400">ack'd {new Date(ca.acknowledged_at).toUTCString().slice(5, 22)}</span>}
            {ca.completed_at && <span className="text-emerald-400">done {new Date(ca.completed_at).toUTCString().slice(5, 22)}</span>}
            {ca.escalated_at && <span className="text-red-400">escalated {new Date(ca.escalated_at).toUTCString().slice(5, 22)}</span>}
          </div>
        </div>

        {/* Lifecycle controls */}
        {!isClosed && !isLegacy && (
          <div className="flex flex-col gap-1.5 shrink-0">
            {ca.status === "open" && (
              <Button size="sm" onClick={() => onUpdate(ca.id, "acknowledge")}
                className="bg-sky-500 hover:bg-sky-600 text-zinc-950 text-[10px] h-7 font-semibold px-3"
                data-testid={`ca-ack-${ca.id}`}>
                Acknowledge
              </Button>
            )}
            <Button size="sm" onClick={() => onUpdate(ca.id, "complete")}
              className="bg-emerald-500 hover:bg-emerald-600 text-zinc-950 text-[10px] h-7 font-semibold px-3"
              data-testid={`ca-complete-${ca.id}`}>
              Complete
            </Button>
            <Button size="sm" variant="outline" onClick={() => onUpdate(ca.id, "escalate")}
              className="border-red-700/60 text-red-300 hover:bg-red-500/10 text-[10px] h-7 px-3"
              data-testid={`ca-escalate-${ca.id}`}>
              Escalate
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

// Typeahead party search across contacts / facilities / organizations
function usePartySearch(query) {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!query || query.length < 2) { setResults([]); return; }
    let cancelled = false;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API}/parties/search`, { params: { q: query, limit: 8 } });
        if (!cancelled) setResults(data.items || []);
      } catch { /* noop */ }
      finally { if (!cancelled) setLoading(false); }
    }, 220);
    return () => { cancelled = true; clearTimeout(t); };
  }, [query]);

  return { results, loading };
}

function AttachPartyDialog({ existing, onClose, onAttached }) {
  const [role, setRole] = useState("consignee");
  const [query, setQuery] = useState("");
  const [picked, setPicked] = useState(null);
  const { results } = usePartySearch(query);

  const submit = () => {
    if (!picked) { toast.error("Pick a party from search results"); return; }
    onAttached([...existing, {
      role,
      party_type: picked.party_type,
      party_id: picked.party_id,
      label: picked.label,
      sub_label: picked.sub_label,
    }]);
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" onClick={onClose}>
      <Card className="bg-zinc-950 border-zinc-800 p-6 w-[520px]" onClick={(e) => e.stopPropagation()}
        data-testid="attach-party-dialog">
        <h3 className="text-sm font-semibold text-zinc-100 mb-1">Attach Party</h3>
        <p className="text-[11px] text-zinc-500 mb-4">
          Link a registered Organization / Facility / Contact to this shipment by role.
        </p>

        <Label className="text-[10px] uppercase tracking-wider text-zinc-400">Role</Label>
        <select value={role} onChange={(e) => setRole(e.target.value)}
          className="w-full mt-1 bg-zinc-900 border border-zinc-800 rounded-md text-sm text-zinc-100 px-2.5 py-2"
          data-testid="attach-role-select">
          {SHIPMENT_PARTY_ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
        </select>

        <Label className="text-[10px] uppercase tracking-wider text-zinc-400 mt-3 block">Search registry</Label>
        <Input value={query} onChange={(e) => { setQuery(e.target.value); setPicked(null); }}
          placeholder="Type a name…"
          className="bg-zinc-900 border-zinc-800 text-sm mt-1"
          data-testid="attach-search-input" />

        {picked ? (
          <div className="mt-2 p-2.5 rounded border border-teal-700/50 bg-teal-500/5">
            <div className="text-[10px] uppercase tracking-wider text-teal-400">{picked.party_type}</div>
            <div className="text-sm font-medium text-zinc-100">{picked.label}</div>
            {picked.sub_label && <div className="text-[10px] text-zinc-500">{picked.sub_label}</div>}
            <button onClick={() => setPicked(null)} className="text-[10px] text-zinc-500 hover:text-zinc-300 mt-1">change</button>
          </div>
        ) : (
          results.length > 0 && (
            <div className="mt-2 max-h-48 overflow-y-auto border border-zinc-800 rounded bg-zinc-900/50">
              {results.map((r) => (
                <button key={`${r.party_type}-${r.party_id}`}
                  onClick={() => setPicked(r)}
                  className="w-full text-left px-3 py-2 hover:bg-zinc-800/50 border-b border-zinc-800 last:border-0"
                  data-testid={`search-result-${r.party_id}`}>
                  <div className="text-[9px] uppercase tracking-wider text-teal-400">{r.party_type}</div>
                  <div className="text-sm text-zinc-100">{r.label}</div>
                  {r.sub_label && <div className="text-[10px] text-zinc-500">{r.sub_label}</div>}
                </button>
              ))}
            </div>
          )
        )}

        <div className="flex justify-end gap-2 mt-5">
          <Button variant="outline" onClick={onClose} className="border-zinc-700 text-xs h-8">Cancel</Button>
          <Button onClick={submit} disabled={!picked}
            className="bg-teal-500 hover:bg-teal-600 text-zinc-950 text-xs h-8 font-semibold"
            data-testid="attach-confirm-btn">
            Attach
          </Button>
        </div>
      </Card>
    </div>
  );
}

function RecordHandoffDialog({ shipmentId, onClose, onRecorded }) {
  const [action, setAction] = useState("received_by");
  const [query, setQuery] = useState("");
  const [picked, setPicked] = useState(null);
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const { results } = usePartySearch(query);

  const submit = async () => {
    if (!picked) { toast.error("Pick a party"); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/shipments/${shipmentId}/handoffs`, {
        action,
        party_type: picked.party_type,
        party_id: picked.party_id,
        note: note.trim() || undefined,
      });
      toast.success("Handoff recorded");
      onRecorded();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to record");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" onClick={onClose}>
      <Card className="bg-zinc-950 border-zinc-800 p-6 w-[520px]" onClick={(e) => e.stopPropagation()}
        data-testid="handoff-dialog">
        <h3 className="text-sm font-semibold text-zinc-100 mb-1 flex items-center gap-2">
          <Hash size={14} className="text-amber-400" weight="duotone" /> Record Chain-of-Custody Handoff
        </h3>
        <p className="text-[11px] text-zinc-500 mb-4">
          Capture who released, received, inspected, or approved this shipment at this point in the chain.
        </p>

        <Label className="text-[10px] uppercase tracking-wider text-zinc-400">Action</Label>
        <select value={action} onChange={(e) => setAction(e.target.value)}
          className="w-full mt-1 bg-zinc-900 border border-zinc-800 rounded-md text-sm text-zinc-100 px-2.5 py-2"
          data-testid="handoff-action-select">
          {HANDOFF_ACTIONS.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
        </select>

        <Label className="text-[10px] uppercase tracking-wider text-zinc-400 mt-3 block">Party</Label>
        <Input value={query} onChange={(e) => { setQuery(e.target.value); setPicked(null); }}
          placeholder="Search by name…"
          className="bg-zinc-900 border-zinc-800 text-sm mt-1"
          data-testid="handoff-search-input" />

        {picked ? (
          <div className="mt-2 p-2.5 rounded border border-amber-700/50 bg-amber-500/5">
            <div className="text-[10px] uppercase tracking-wider text-amber-400">{picked.party_type}</div>
            <div className="text-sm font-medium text-zinc-100">{picked.label}</div>
            {picked.sub_label && <div className="text-[10px] text-zinc-500">{picked.sub_label}</div>}
            <button onClick={() => setPicked(null)} className="text-[10px] text-zinc-500 hover:text-zinc-300 mt-1">change</button>
          </div>
        ) : (
          results.length > 0 && (
            <div className="mt-2 max-h-44 overflow-y-auto border border-zinc-800 rounded bg-zinc-900/50">
              {results.map((r) => (
                <button key={`${r.party_type}-${r.party_id}`}
                  onClick={() => setPicked(r)}
                  className="w-full text-left px-3 py-2 hover:bg-zinc-800/50 border-b border-zinc-800 last:border-0">
                  <div className="text-[9px] uppercase tracking-wider text-amber-400">{r.party_type}</div>
                  <div className="text-sm text-zinc-100">{r.label}</div>
                  {r.sub_label && <div className="text-[10px] text-zinc-500">{r.sub_label}</div>}
                </button>
              ))}
            </div>
          )
        )}

        <Label className="text-[10px] uppercase tracking-wider text-zinc-400 mt-3 block">Note (optional)</Label>
        <textarea value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} rows={2}
          placeholder="e.g. cargo verified at dock 4, probe 39°F"
          className="w-full mt-1 bg-zinc-900 border border-zinc-800 rounded-md text-sm text-zinc-100 px-2.5 py-2 resize-none focus:outline-none focus:ring-1 focus:ring-amber-500"
          data-testid="handoff-note" />

        <div className="flex justify-end gap-2 mt-5">
          <Button variant="outline" onClick={onClose} className="border-zinc-700 text-xs h-8">Cancel</Button>
          <Button onClick={submit} disabled={submitting || !picked}
            className="bg-amber-500 hover:bg-amber-600 text-zinc-950 text-xs h-8 font-semibold"
            data-testid="handoff-submit-btn">
            Record Handoff
          </Button>
        </div>
      </Card>
    </div>
  );
}




/**
 * EvidencePackageButton — flagship CTA on every shipment.
 *
 * Generates a signed, multi-section PDF that bundles the entire shipment
 * investigation: summary + AI assessment + route + sensor charts + audit
 * trail + corrective actions + integrity verification. The QR code in the
 * PDF resolves to a public, token-secured verification URL.
 *
 * Lives in the shipment header so it is always reachable (not gated behind
 * the Compliance tab, which only renders when a compliance score exists).
 */
function EvidencePackageButton({ shipmentId }) {
  const [busy, setBusy] = useState(false);
  const onClick = async () => {
    setBusy(true);
    try {
      const res = await axios.get(`${API}/evidence/shipments/${shipmentId}/generate`, {
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const fname = res.headers["content-disposition"]?.match(/filename=([^;]+)/)?.[1]
        || `evidence_${shipmentId.slice(0, 8)}.pdf`;
      a.download = fname.trim().replace(/^"|"$/g, "");
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
      const verifyUrl = res.headers["x-evidence-verify-url"];
      toast.success(
        verifyUrl
          ? "Evidence package downloaded · public verify URL embedded in PDF"
          : "Evidence package downloaded",
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Evidence export failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Button onClick={onClick} disabled={busy}
      className="bg-violet-500 hover:bg-violet-400 text-zinc-50 text-xs h-9 font-semibold"
      data-testid="download-evidence-package-btn"
      title="Generate a signed, public-verifiable shipment investigation packet">
      <ShieldCheck size={12} className="mr-1.5" weight="duotone" />
      {busy ? "Generating…" : "Evidence Package"}
    </Button>
  );
}

/**
 * HeldSummariesBanner — Admin-only banner that lists logger import summaries
 * which were held by the dispatch gate (e.g., because the shipment was not
 * yet `active` when the import landed). One click per row re-fires the
 * summary email/SMS to the originally-resolved recipients.
 */
function HeldSummariesBanner({ shipmentId, user, lifecycleState, onChanged }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const isAdmin = ["super_admin", "admin", "org_admin"].includes(user?.role);

  const fetchHeld = useCallback(async () => {
    if (!isAdmin || !shipmentId) { setLoading(false); return; }
    try {
      const r = await axios.get(`${API}/logger-imports/shipments/${shipmentId}/held-summaries`);
      setRows(r.data?.held || []);
    } catch (e) { void e; }
    finally { setLoading(false); }
  }, [shipmentId, isAdmin]);

  useEffect(() => { fetchHeld(); }, [fetchHeld]);

  const redispatch = async (importId) => {
    setBusyId(importId);
    try {
      const res = await axios.post(`${API}/logger-imports/${importId}/redispatch-summary`);
      if (res.data?.dispatched) {
        toast.success(`Held summary dispatched to ${res.data.recipients} recipient(s)`);
      } else {
        toast.warning(`Summary still held: ${res.data?.reason || "unknown reason"}`);
      }
      await fetchHeld();
      if (onChanged) onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Re-dispatch failed");
    } finally {
      setBusyId(null);
    }
  };

  if (!isAdmin || loading || rows.length === 0) return null;

  const blockedByLifecycle = lifecycleState && lifecycleState !== "active";

  return (
    <Card className="bg-amber-500/5 border-amber-500/30 p-3"
      data-testid="held-summaries-banner">
      <div className="flex items-start gap-2">
        <Bell size={14} weight="duotone" className="text-amber-300 mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-xs font-semibold text-amber-200">
            {rows.length} logger import summar{rows.length === 1 ? "y" : "ies"} held
          </div>
          <div className="text-[11px] text-amber-200/80 mt-0.5">
            {blockedByLifecycle ? (
              <>Activate the shipment first, then re-dispatch — the gate will continue to hold otherwise.</>
            ) : (
              <>Shipment is active. Click <b>Re-dispatch</b> below to send each summary now.</>
            )}
          </div>
          <div className="mt-2 space-y-1">
            {rows.map((r) => (
              <div key={r.import_id}
                className="flex items-center justify-between gap-2 bg-zinc-950/40 border border-zinc-800 rounded-sm px-2 py-1.5"
                data-testid={`held-summary-row-${r.import_id}`}>
                <div className="min-w-0 flex-1">
                  <div className="text-[11px] text-zinc-200 truncate font-mono">
                    {r.filename || r.import_id.slice(0, 8)}
                  </div>
                  <div className="text-[10px] text-zinc-500 mt-0.5">
                    Held {(r.held_at || "").slice(0, 19)} ·{" "}
                    reason: <span className="text-amber-300">{r.reason || "—"}</span> ·{" "}
                    {r.recipient_count ?? 0} recipient(s) ·{" "}
                    {r.alerts_generated ?? 0} alert(s)
                  </div>
                </div>
                <Button onClick={() => redispatch(r.import_id)}
                  disabled={busyId === r.import_id}
                  size="sm" variant="outline"
                  className="border-amber-700/60 text-amber-200 hover:bg-amber-500/10 text-[10px] h-7"
                  data-testid={`held-summary-redispatch-btn-${r.import_id}`}>
                  {busyId === r.import_id ? "Sending…" : "Re-dispatch"}
                </Button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Card>
  );
}
